/**
 * Step two: where should your code live?
 *
 * Two answers, and the first is selected for you. Most people who build here
 * have no GitHub account and should not need one, so "Store code with SV Cloud"
 * is the default and says so in the words a person who has never heard of git
 * would use. The GitHub choice is the same flow it always was.
 *
 * Choosing SV Cloud creates the project right here, with no repository to pick,
 * and hands over to the build screen. Choosing GitHub goes on to the existing
 * connect screen untouched. The choice is kept in the draft so the build
 * screen's retry knows which kind of project it is retrying.
 */

import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useAccount } from '../../lib/auth/use-app-data';

import { BackLink } from './BackLink';
import { Notice } from '../../components/patterns/Notice';
import { StepDots } from '../../components/patterns/StepDots';
import { Button } from '../../components/primitives/Button';

import { captureEvent } from '../../lib/analytics/posthog';
import { ApiError } from '../../lib/http';
import { t } from '../../lib/i18n/t';
import { ONBOARDING_STEPS } from '../../lib/mock/onboarding';
import { clearDraft, readDraft, writeDraft, type CodeHost as CodeHostChoice } from '../../lib/onboarding/draft';
import { connectProject } from '../../lib/real-api';

import '../../styles/screens/onboarding.css';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

function connectErrorMessage(code: string): string {
  switch (code) {
    case 'SUBDOMAIN_TAKEN':
      return t('That web address was just taken. Go back and pick another name.');
    case 'PROJECT_QUOTA_EXCEEDED':
      return t('This account is already using all the active projects it may have.');
    case 'SVGIT_NOT_AVAILABLE':
      return t('Storing code with SV Cloud is not available yet. Continue with GitHub instead.');
    default:
      return t('Something went wrong connecting your project. Try again.');
  }
}

export default function CodeHost() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const draft = useMemo(() => readDraft(), []);
  const { svgitEnabled } = useAccount();
  const app = params.get('app') ?? draft?.slug ?? 'herb-and-ash';

  const [choice, setChoice] = useState<CodeHostChoice>(draft?.codeHost ?? 'svgit');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function pick(next: CodeHostChoice) {
    setChoice(next);
    setError(null);
  }

  function onContinue() {
    captureEvent('create_code_host_chosen', { code_host: choice });
    if (draft) writeDraft({ ...draft, codeHost: choice });

    if (choice === 'github') {
      navigate(`/new/connect?app=${app}`);
      return;
    }

    if (USE_MOCK) {
      navigate(`/new/building?app=${app}&host=svgit`);
      return;
    }

    if (!draft) {
      navigate('/new');
      return;
    }
    setBusy(true);
    setError(null);
    connectProject({ name: draft.name, codeHost: 'svgit', requestedSubdomain: draft.slug })
      .then((result) => {
        clearDraft();
        if (result.runId) {
          navigate(`/new/building?project=${result.projectId}&run=${result.runId}&slug=${result.slug}&host=svgit`, {
            state: { name: draft.name, codeHost: 'svgit' },
          });
        } else {
          navigate(`/p/${result.slug}`);
        }
      })
      .catch((err: unknown) => {
        captureEvent('connect_code_failed', {
          code_host: 'svgit',
          error_code: err instanceof ApiError ? err.code : 'unknown',
        });
        setError(connectErrorMessage(err instanceof ApiError ? err.code : 'unknown'));
        setBusy(false);
      });
  }

  const options: { value: CodeHostChoice; title: string; note: string; easiest?: boolean }[] = [
    {
      value: 'svgit',
      title: t('Store code with SV Cloud'),
      note: t('Nothing else to sign up for. You can move it to GitHub later.'),
      easiest: true,
    },
    {
      value: 'github',
      title: t('Store code with GitHub'),
      note: t('For people who already use GitHub.'),
    },
  ];

  // A deep link to this step on an account without SV Git: nothing to choose.
  if (!svgitEnabled) return <Navigate to={`/new/connect?app=${app}`} replace />;

  return (
    <div className="onb">
      <StepDots steps={ONBOARDING_STEPS.map((step) => t(step))} current={1} label={t('Setting up your project')} />

      <div className="onb__col">
        <BackLink to="/new" />
        <header className="onb__head">
          <h1 className="onb__title">{t('Where should your code live?')}</h1>
        </header>

        <form
          className="onb__form"
          onSubmit={(event) => {
            event.preventDefault();
            if (!busy) onContinue();
          }}
        >
          <div className="codehost" role="radiogroup" aria-label={t('Where should your code live?')}>
            {options.map((option) => (
              <label
                key={option.value}
                className={option.easiest ? 'codehost__option' : 'codehost__option codehost__option--compact'}
                data-selected={choice === option.value ? 'yes' : 'no'}
              >
                <input
                  className="codehost__radio"
                  type="radio"
                  name="code-host"
                  value={option.value}
                  checked={choice === option.value}
                  onChange={() => pick(option.value)}
                />
                <span className="codehost__title">
                  {option.title}
                  {option.easiest ? <span className="codehost__tag">{t('Easiest')}</span> : null}
                </span>
                <span className="codehost__note">{option.note}</span>
              </label>
            ))}
          </div>

          {error ? (
            <Notice tone="attention">
              <p>{error}</p>
            </Notice>
          ) : null}

          <div className="onb__actions">
            <Button variant="solid" type="submit" loading={busy}>
              {busy ? t('Setting up code storage') : t('Continue')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
